import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { collection, getDocs, doc, updateDoc, deleteDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { categoryLabel } from "../data/categories";
import { isLive } from "../hooks/useVendors";
import BoostAdmin from "../components/BoostAdmin";
import PlanAdmin from "../components/PlanAdmin";
import usePlanSettings, { priceFor, fmtPrice } from "../hooks/usePlanSettings";
import { formatPrice } from "../lib/price";

const MONTH_OPTIONS = [1, 3, 6, 12];

const toMillis = (ts) =>
  ts && ts.toMillis ? ts.toMillis() : ts && ts.seconds ? ts.seconds * 1000 : null;

const fmtDate = (ts) => {
  const ms = toMillis(ts);
  return ms ? new Date(ms).toLocaleDateString() : "—";
};

const displayStatus = (v) =>
  !v.isActive
    ? v.status === "expired"
      ? "expired"
      : "pending"
    : isLive(v)
    ? "active"
    : "expired";

export default function Admin() {
  const { t } = useLang();
  const { user, isAdmin, loading, login, logout } = useAuth();
  if (loading) return <Center>…</Center>;
  if (!user) return <Login t={t} login={login} />;
  if (!isAdmin)
    return (
      <Center>
        <LoginCard>
          <h1>{t("admin.notAuthorized")}</h1>
          <button onClick={logout}>{t("admin.logout")}</button>
        </LoginCard>
      </Center>
    );
  return <Dashboard t={t} logout={logout} />;
}

/* ---------- Login ---------- */
function Login({ t, login }) {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      await login(email, password);
    } catch {
      setError(t("admin.loginError"));
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    setResetSent(false);
    if (!email.trim()) {
      setError(t("auth.enterEmail"));
      return;
    }
    try {
      await resetPassword(email.trim());
      setError("");
      setResetSent(true);
    } catch {
      setError(t("auth.resetError"));
    }
  };

  return (
    <Center>
      <LoginCard>
        <h1>{t("admin.loginTitle")}</h1>
        <input type="email" placeholder={t("admin.email")} value={email} onChange={(e) => setEmail(e.target.value)} />
        <input type="password" placeholder={t("admin.password")} value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
        {error && <span className="err">{error}</span>}
        {resetSent && <span className="ok">{t("auth.resetSent")}</span>}
        <button onClick={submit} disabled={busy}>{t("admin.login")}</button>
        <button type="button" className="forgot" onClick={forgot}>{t("auth.forgot")}</button>
        <Link to="/">← {t("list.backHome")}</Link>
      </LoginCard>
    </Center>
  );
}

/* ---------- Dashboard ---------- */
function Dashboard({ t, logout }) {
  const { lang } = useLang();
  const { settings: plans, founding } = usePlanSettings();
  const [vendors, setVendors] = useState([]);
  const [tab, setTab] = useState("pending");
  const [forms, setForms] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, "vendors"));
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    list.sort((a, b) => {
      if (a.status === "pending" && b.status !== "pending") return -1;
      if (b.status === "pending" && a.status !== "pending") return 1;
      return (toMillis(b.createdAt) || 0) - (toMillis(a.createdAt) || 0);
    });
    setVendors(list);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const getMonths = (id) => forms[id]?.months ?? 12;
  const getAmount = (id) => forms[id]?.amount ?? "";
  const setField = (id, key, value) =>
    setForms((f) => ({ ...f, [id]: { ...f[id], [key]: value } }));

  const activate = async (v) => {
    const months = getMonths(v.id);
    const amount = Number(getAmount(v.id)) || 0;
    const start = new Date();
    const end = new Date();
    end.setMonth(end.getMonth() + months);
    await updateDoc(doc(db, "vendors", v.id), {
      isActive: true,
      status: "active",
      activatedAt: Timestamp.fromDate(start),
      paidUntil: Timestamp.fromDate(end),
      subscriptionMonths: months,
      amountPaid: amount,
    });
    load();
  };

  const deactivate = async (v) => {
    await updateDoc(doc(db, "vendors", v.id), { isActive: false, status: "expired" });
    load();
  };

  const remove = async (v) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    await deleteDoc(doc(db, "vendors", v.id));
    load();
  };

  const shown = useMemo(() => {
    if (tab === "all") return vendors;
    if (tab === "pending") return vendors.filter((v) => v.status === "pending");
    return vendors.filter((v) => v.isActive);
  }, [vendors, tab]);

  const counts = useMemo(
    () => ({
      pending: vendors.filter((v) => v.status === "pending").length,
      active: vendors.filter((v) => v.isActive).length,
      all: vendors.length,
    }),
    [vendors]
  );

  const statusLabel = (s) =>
    s === "active" ? t("admin.statusActive") : s === "expired" ? t("admin.statusExpired") : t("admin.statusPending");

  return (
    <Wrap>
      <TopBar>
        <h1>{t("admin.title")}</h1>
        <button onClick={logout}>{t("admin.logout")}</button>
      </TopBar>

      <Tabs>
        {["pending", "active", "boosts", "plans", "all"].map((k) => (
          <button key={k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>
            {t(`admin.${k}`)} {counts[k] != null && <em>{counts[k]}</em>}
          </button>
        ))}
      </Tabs>

      {tab === "boosts" ? (
        <BoostAdmin />
      ) : tab === "plans" ? (
        <PlanAdmin />
      ) : loading ? (
        <Empty>…</Empty>
      ) : shown.length === 0 ? (
        <Empty>{t("admin.noVendors")}</Empty>
      ) : (
        <List>
          {shown.map((v) => {
            const ds = displayStatus(v);
            const live = ds === "active";
            return (
              <VendorRow key={v.id}>
                <RowTop>
                  <Thumb>
                    {v.photos && v.photos[0] ? <img src={v.photos[0]} alt="" /> : <div className="ph" />}
                  </Thumb>
                  <Info>
                    <strong>{v.businessName}</strong>
                    <span>
                      {categoryLabel(v.category, lang)} · {v.city} · {formatPrice(v, lang === "sq") || t("price.none")} ·{" "}
                      {v.accountType === "company" ? t("card.company") : t("card.individual")}
                    </span>
                    <Badge $status={ds}>{statusLabel(ds)}</Badge>
                    <TierTag $premium={v.tier === "premium"}>
                      {t(`plan.${v.tier === "premium" ? "premium" : "basic"}`)} · {fmtPrice(priceFor(plans, v.category, v.tier === "premium" ? "premium" : "basic", founding))}/{t("plan.mo")}
                    </TierTag>
                  </Info>
                  <button className="del" onClick={() => remove(v)}>{t("admin.delete")}</button>
                </RowTop>

                {live && <SubPanel v={v} t={t} />}

                <Manage>
                  <Group>
                    <label>{t("admin.duration")}</label>
                    <select value={getMonths(v.id)} onChange={(e) => setField(v.id, "months", Number(e.target.value))}>
                      {MONTH_OPTIONS.map((m) => (
                        <option key={m} value={m}>{m} {t("admin.months")}</option>
                      ))}
                    </select>
                  </Group>
                  <Group>
                    <label>{t("admin.amount")}</label>
                    <input type="number" min="0" value={getAmount(v.id)} onChange={(e) => setField(v.id, "amount", e.target.value)} placeholder="€" />
                  </Group>
                  <Buttons>
                    <button className="go" onClick={() => activate(v)}>
                      {live ? t("admin.renew") : t("admin.activate")}
                    </button>
                    {live && <button className="warn" onClick={() => deactivate(v)}>{t("admin.deactivate")}</button>}
                  </Buttons>
                </Manage>
              </VendorRow>
            );
          })}
        </List>
      )}
    </Wrap>
  );
}

/* ---------- Subscription panel ---------- */
function SubPanel({ v, t }) {
  const start = toMillis(v.activatedAt);
  const end = toMillis(v.paidUntil);
  const now = Date.now();
  const pct =
    start && end && end > start
      ? Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100))
      : end && now < end
      ? 5
      : 100;
  const daysLeft = end ? Math.max(0, Math.ceil((end - now) / 86400000)) : null;
  const danger = daysLeft != null && daysLeft <= 14;

  return (
    <Sub>
      <SubStats>
        {v.amountPaid != null && (
          <span><b>{t("admin.paid")}:</b> €{v.amountPaid}</span>
        )}
        <span><b>{t("admin.starts")}:</b> {fmtDate(v.activatedAt)}</span>
        <span><b>{t("admin.ends")}:</b> {fmtDate(v.paidUntil)}</span>
        {daysLeft != null && (
          <span className={danger ? "danger" : "ok"}>{daysLeft} {t("admin.daysLeft")}</span>
        )}
      </SubStats>
      <Bar>
        <BarFill $danger={danger} style={{ width: `${pct}%` }} />
      </Bar>
    </Sub>
  );
}

/* ---------- styles ---------- */
const Center = styled.div`
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 24px;
`;

const LoginCard = styled.div`
  width: 100%;
  max-width: 360px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.hair};
  border-radius: ${({ theme }) => theme.radius};
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  h1 { font-family: ${({ theme }) => theme.font.display}; font-weight: 500; font-size: 28px; color: ${({ theme }) => theme.colors.green}; margin-bottom: 6px; }
  input { font-family: inherit; font-size: 15px; border: 1px solid ${({ theme }) => theme.colors.hair}; border-radius: 12px; padding: 12px 14px; outline: none; background: ${({ theme }) => theme.colors.cream}; }
  input:focus { border-color: ${({ theme }) => theme.colors.green}; }
  .err { color: ${({ theme }) => theme.colors.rose}; font-size: 13px; }
  .ok { color: ${({ theme }) => theme.colors.green}; font-size: 13px; }
  .forgot { background: transparent; border: none; color: ${({ theme }) => theme.colors.inkSoft}; font-size: 13px; cursor: pointer; text-decoration: underline; padding: 0; }
  .forgot:hover { color: ${({ theme }) => theme.colors.green}; }
  button { background: ${({ theme }) => theme.colors.green}; color: ${({ theme }) => theme.colors.cream}; border: none; border-radius: 100px; padding: 13px; font-size: 15px; font-weight: 500; cursor: pointer; }
  a { font-size: 13px; color: ${({ theme }) => theme.colors.inkSoft}; text-align: center; }
`;

const Wrap = styled.div`
  max-width: 920px;
  margin: 0 auto;
  padding: 32px clamp(16px, 5vw, 40px) 80px;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 22px;
  h1 { font-family: ${({ theme }) => theme.font.display}; font-weight: 500; font-size: 30px; color: ${({ theme }) => theme.colors.ink}; }
  button { background: transparent; border: 1px solid ${({ theme }) => theme.colors.hair}; border-radius: 100px; padding: 9px 18px; font-size: 14px; cursor: pointer; color: ${({ theme }) => theme.colors.inkSoft}; }
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  button { background: transparent; border: 1px solid ${({ theme }) => theme.colors.hair}; border-radius: 100px; padding: 8px 16px; font-size: 14px; font-weight: 500; cursor: pointer; color: ${({ theme }) => theme.colors.inkSoft}; em { font-style: normal; opacity: 0.6; margin-left: 4px; } }
  button.on { background: ${({ theme }) => theme.colors.green}; color: ${({ theme }) => theme.colors.cream}; border-color: ${({ theme }) => theme.colors.green}; }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const VendorRow = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.hair};
  border-radius: 14px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const RowTop = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  .del { background: transparent; border: none; color: ${({ theme }) => theme.colors.rose}; cursor: pointer; font-size: 13px; font-weight: 500; }
`;

const Thumb = styled.div`
  flex: 0 0 56px;
  img, .ph { width: 56px; height: 56px; border-radius: 10px; object-fit: cover; background: ${({ theme }) => theme.colors.creamDeep}; }
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  strong { font-size: 16px; }
  span { font-size: 13px; color: ${({ theme }) => theme.colors.inkSoft}; }
`;

const Badge = styled.span`
  align-self: flex-start;
  margin-top: 2px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 100px;
  color: ${({ $status, theme }) => ($status === "active" ? theme.colors.green : $status === "expired" ? theme.colors.rose : "#9a7b1f")};
  background: ${({ $status }) => ($status === "active" ? "rgba(47,74,60,.1)" : $status === "expired" ? "rgba(217,140,130,.15)" : "rgba(201,182,142,.22)")};
`;

const Sub = styled.div`
  background: ${({ theme }) => theme.colors.cream};
  border: 1px solid ${({ theme }) => theme.colors.hair};
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SubStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.inkSoft};
  b { color: ${({ theme }) => theme.colors.ink}; font-weight: 600; }
  .ok { color: ${({ theme }) => theme.colors.green}; font-weight: 600; }
  .danger { color: ${({ theme }) => theme.colors.rose}; font-weight: 600; }
`;

const Bar = styled.div`
  height: 8px;
  background: ${({ theme }) => theme.colors.creamDeep};
  border-radius: 100px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 100px;
  background: ${({ $danger, theme }) => ($danger ? theme.colors.rose : theme.colors.green)};
  transition: width 0.4s ease;
`;

const Manage = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  label { font-size: 12px; color: ${({ theme }) => theme.colors.inkSoft}; }
  select, input {
    font-family: inherit; font-size: 14px;
    border: 1px solid ${({ theme }) => theme.colors.hair};
    border-radius: 10px; padding: 9px 12px; background: ${({ theme }) => theme.colors.cream}; outline: none;
    width: 120px;
  }
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
  button { border: none; border-radius: 10px; padding: 10px 16px; font-size: 13px; font-weight: 500; cursor: pointer; }
  .go { background: ${({ theme }) => theme.colors.green}; color: ${({ theme }) => theme.colors.cream}; }
  .warn { background: ${({ theme }) => theme.colors.creamDeep}; color: ${({ theme }) => theme.colors.ink}; }
`;

const Empty = styled.div`
  padding: 60px 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.inkSoft};
`;

const TierTag = styled.span`
  align-self: flex-start;
  margin-top: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 100px;
  color: ${({ $premium, theme }) => ($premium ? theme.colors.greenDeep : theme.colors.inkSoft)};
  background: ${({ $premium, theme }) => ($premium ? theme.colors.gold : theme.colors.creamDeep)};
`;
